import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Accordion from '../../components/ui/Accordion';
import Header from '../../components/ui/Header';
import { supabase } from '../../lib/supabase';
import ElevenLabsAgentWrapper from '../../components/ElevenLabsAgentWrapper';
import Turnstile from '../../components/Turnstile';
import Image from '../../components/AppImage';

const BOOKING_URL = 'https://outlook.office.com/bookwithme/user/48e6b21b395043f780bc299577a97fee@wigbers-automation.de?anonymous&ismsaljsauthenabled&ep=plink';

const AgentOpsPage = () => {
  const [showVoiceAgent, setShowVoiceAgent] = useState(false);
  const [formData, setFormData] = useState({ name: '', company: '', email: '', phone: '', message: '' });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileError, setTurnstileError] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name ist erforderlich';
    if (!formData.company.trim()) errors.company = 'Firma ist erforderlich';
    if (!formData.email.trim()) errors.email = 'E-Mail ist erforderlich';
    if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Ungültige E-Mail-Adresse';
    if (!formData.phone.trim()) errors.phone = 'Telefonnummer ist erforderlich';
    if (!formData.message.trim()) errors.message = 'Nachricht ist erforderlich';
    if (!turnstileToken) {
      errors.turnstile = 'Bitte bestätigen Sie, dass Sie kein Roboter sind';
      setTurnstileError('Bitte bestätigen Sie, dass Sie kein Roboter sind');
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setTurnstileError(null);
    try {
      if (turnstileToken) {
        const { data: verifyData, error: verifyError } = await supabase?.functions?.invoke('verify-turnstile', { body: { token: turnstileToken } });
        if (verifyError || !verifyData?.success) {
          setFormErrors({ submit: 'Verifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.' });
          setTurnstileToken(null);
          setTurnstileKey(prev => prev + 1);
          setIsSubmitting(false);
          return;
        }
      }
      const { data, error } = await supabase?.functions?.invoke('send-mail', {
        body: { name: formData.name.trim(), company: formData.company.trim(), email: formData.email.toLowerCase().trim(), phone: formData.phone.trim(), message: formData.message.trim() }
      });
      if (error) {
        setFormErrors({ submit: error?.message || 'Fehler beim Senden.' });
      } else if (data?.error) {
        setFormErrors({ submit: data.error });
      } else if (data?.success) {
        setIsSubmitted(true);
      } else {
        setFormErrors({ submit: 'Unerwartete Antwort. Bitte erneut versuchen.' });
      }
      setTurnstileToken(null);
      setTurnstileKey(prev => prev + 1);
    } catch {
      setFormErrors({ submit: 'Ein Fehler ist aufgetreten.' });
      setTurnstileToken(null);
      setTurnstileKey(prev => prev + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqItems = [
    { question: 'Gibt es Fördermöglichkeiten?', answer: 'Ja. Je nach Standort und Unternehmensgröße sind Zuschüsse über BAFA (bis 80% der Beratungskosten), NBank, GRW-Förderung oder Landesprogramme wie den Digitalbonus möglich. Im Erstgespräch machen wir einen kurzen Förder-Check.' },
    { question: 'Warum Open Source statt etablierter SaaS?', answer: 'Open-Source-Lösungen sind kosteneffizienter als Abo-Modelle — ohne Abstriche bei Sicherheit oder Funktionalität. Sie behalten die volle Kontrolle über Ihre Daten und sind nicht von Preiserhöhungen einzelner Anbieter abhängig.' },
    { question: 'Wie schnell sind erste Ergebnisse sichtbar?', answer: 'Nach der Analysephase (ca. 2 Wochen) steht der Migrationsfahrplan. Erste Migrationen und Automatisierungen gehen typischerweise nach 4–6 Wochen live.' },
    { question: 'Wie sicher sind meine Daten?', answer: 'Datenschutz hat oberste Priorität. Durch Open-Source-Lösungen können Daten lokal oder in EU-Instanzen verarbeitet werden. Alle Lösungen arbeiten DSGVO-konform mit strikten Zugriffskontrollen.' },
    { question: 'Was kostet die Transformation?', answer: 'Durch die Kombination aus eingesparten Lizenzgebühren und Förderprogrammen ist die Modernisierung meist kostenneutral. Im Erstgespräch erstelle ich eine individuelle ROI-Kalkulation.' },
    { question: 'Wie aufwendig ist das für mein Team?', answer: 'Nach einem initialen Briefing übernehme ich die Umsetzung. Migration läuft im Parallelbetrieb — kein Produktivitätsverlust. Am Ende gibt es Schulungen für Ihr Team.' },
  ];

  return (
    <>
      <Helmet>
        <title>Stefan Wigbers — Open Source AI Transformation</title>
        <meta name="description" content="Teure Lizenzverträge durch Open-Source ersetzen und Prozesse mit KI automatisieren. BAFA-zertifizierte Beratung — meist kostenneutral durch Förderprogramme." />
        <meta property="og:title" content="Stefan Wigbers — Open Source AI Transformation" />
        <meta property="og:description" content="SaaS-Kosten senken, Prozesse mit KI automatisieren — meist kostenneutral durch Förderprogramme." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://wigbers-automation.de" />
      </Helmet>

      <div className="min-h-screen bg-white">
        <Header />

        <main className="pt-16">

          {/* ===================== HERO ===================== */}
          <section className="relative min-h-[calc(100svh-4rem)] md:min-h-[90vh] flex items-start md:items-center bg-[#fafafa]">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full py-8 sm:py-16 md:py-32">
              <div className="grid lg:grid-cols-12 gap-8 lg:gap-8 items-center">
                <div className="lg:col-span-7 space-y-5 sm:space-y-8">
                  <div className="space-y-4 sm:space-y-6">
                    <p className="text-sm font-mono uppercase tracking-[0.2em] text-gray-400">
                      Stefan Wigbers — IT-Transformation
                    </p>
                    <h1 className="text-[2rem] sm:text-[2.5rem] md:text-[3rem] lg:text-[3.5rem] font-bold text-gray-900 leading-[1.15] tracking-tight">
                      Teure Lizenzen und{' '}
                      <br className="hidden sm:block" />
                      Prozesse, die{' '}
                      <span className="text-[#1E40AF]">nicht fließen?</span>
                    </h1>
                    <div className="space-y-3 sm:space-y-5 max-w-xl">
                      <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed">
                        Ich unterstütze Sie dabei, teure SaaS- und Software-Lizenzverträge durch <strong className="text-gray-900 font-semibold">AI-ready Open-Source-Lösungen</strong> zu ersetzen und manuelle Aufgaben in den Tools durch <strong className="text-gray-900 font-semibold">Agenten</strong> zu automatisieren.
                      </p>
                      <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                        Durch die Kombination aus eingesparten Lizenzgebühren und Förderprogrammen <strong className="text-gray-900 font-semibold">amortisiert sich Ihre neue Infrastruktur bereits nach kurzer Zeit</strong>.
                      </p>
                      <p className="text-base sm:text-lg font-medium text-gray-900">
                        Das Ergebnis: Eine IT, die Ihnen gehört und sich Ihren Abläufen anpasst — nicht umgekehrt.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={() => setShowVoiceAgent(true)}
                      className="inline-flex items-center justify-center gap-2.5 bg-gray-900 text-white px-7 py-3.5 text-[15px] font-medium rounded-lg hover:bg-gray-800 transition-colors"
                    >
                      <Icon name="Mic" size={17} />
                      Potenzialanalyse via Voice AI
                    </button>
                    <button
                      onClick={() => window.open(BOOKING_URL, '_blank')}
                      className="inline-flex items-center justify-center gap-2.5 bg-white text-gray-900 border border-gray-200 px-7 py-3.5 text-[15px] font-medium rounded-lg hover:border-gray-400 transition-colors"
                    >
                      <Icon name="Calendar" size={17} />
                      Termin buchen
                    </button>
                  </div>

                </div>

                {/* Right side — trust signals: compact badges on mobile, full cards on sm+ */}
                <div className="lg:col-span-5">
                  {/* Mobile: compact inline badges */}
                  <div className="flex sm:hidden flex-wrap gap-2 mt-1">
                    <div className="flex items-center gap-2 bg-white border border-gray-100 rounded-lg px-3 py-2">
                      <img src="/assets/logos/bafa.svg" alt="BAFA" className="w-8 h-auto" />
                      <span className="text-xs font-semibold text-gray-900">BAFA-zertifiziert</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white border border-gray-100 rounded-lg px-3 py-2">
                      <div className="w-7 h-7 bg-gray-900 rounded flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-white">6+</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-900">Jahre Erfahrung</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white border border-gray-100 rounded-lg px-3 py-2">
                      <img src="/assets/logos/istqb.svg" alt="ISTQB" className="w-8 h-auto" />
                      <span className="text-xs font-semibold text-gray-900">ISTQB-zertifiziert</span>
                    </div>
                  </div>

                  {/* Desktop: full cards */}
                  <div className="hidden sm:flex flex-col gap-5">
                    {/* BAFA */}
                    <div className="bg-white border border-gray-100 rounded-xl p-6 hover:border-gray-200 transition-colors">
                      <div className="flex items-start gap-5">
                        <img src="/assets/logos/bafa.svg" alt="BAFA Logo" className="w-16 h-auto flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-[17px] font-semibold text-gray-900 mb-1">BAFA-zertifiziert</p>
                          <p className="text-sm text-gray-500 leading-relaxed">Förderfähig — bis zu 80 % der Beratungskosten erstattungsfähig</p>
                        </div>
                      </div>
                    </div>

                    {/* Erfahrung */}
                    <div className="bg-white border border-gray-100 rounded-xl p-6 hover:border-gray-200 transition-colors">
                      <div className="flex items-start gap-5">
                        <div className="w-14 h-14 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-xl font-bold text-white leading-none">6+</span>
                        </div>
                        <div>
                          <p className="text-[17px] font-semibold text-gray-900 mb-1">Jahre Erfahrung</p>
                          <p className="text-sm text-gray-500 leading-relaxed">Process Automation mit LLM-Integration von der Planung bis zur Implementierung</p>
                        </div>
                      </div>
                    </div>

                    {/* ISTQB */}
                    <div className="bg-white border border-gray-100 rounded-xl p-6 hover:border-gray-200 transition-colors">
                      <div className="flex items-start gap-5">
                        <img src="/assets/logos/istqb.svg" alt="ISTQB Logo" className="w-16 h-auto flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-[17px] font-semibold text-gray-900 mb-1">ISTQB Qualitätssicherung</p>
                          <p className="text-sm text-gray-500 leading-relaxed">Qualitätssicherung nach internationalem Standard — strukturiertes Testing bei jeder Implementierung</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>


          {/* ===================== PROJEKTREFERENZEN ===================== */}
          <section id="referenzen" className="py-24 md:py-32 bg-white border-t border-gray-100">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-12">
              <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-300 mb-4">Projektreferenzen</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 max-w-2xl">
                Projekte, die ich umsetzen durfte
              </h2>
              <p className="text-gray-500 max-w-2xl leading-relaxed">
                Ein Auszug realisierter Automatisierungs- und KI-Projekte — von der Konzeption bis zur produktiven Umsetzung.
              </p>
            </div>

            {/* Scrolling Banner */}
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="relative overflow-hidden rounded-xl">
                <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
                <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
                <style>{`
                  @keyframes scrollLeft {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(calc(-50% - 12px)); }
                  }
                  .scroll-banner {
                    animation: scrollLeft 20s linear infinite;
                  }
                  @media (min-width: 640px) {
                    .scroll-banner {
                      animation-duration: 35s;
                    }
                  }
                `}</style>
                {(() => {
                  const items = [
                    {
                      project: 'Konzeption & Umsetzung der Automatisierung der Bonitätsprüfung mit Open-Source-LLMs',
                      company: 'InvoiceFunnel.de',
                    },
                    {
                      project: 'Aufbau einer Open-Source-AI-Plattform (n8n, Local LLM, Langfuse, Guardrails AI) mit DevOps-Pipeline zum Deployment von Workflows mit LLM-Integration',
                      company: 'Laufend — Vertraulichkeit vereinbart',
                      linkedIn: true,
                    },
                    {
                      project: 'Open-Source-CRM-Lösung mit LLM-Integration zur Automatisierung der Erstellung von Kostenvoranschlägen und Bearbeitung von Leads',
                      company: 'NE Neue Energien GmbH',
                    },
                    {
                      project: 'Konzeption & Umsetzung eines automatisierten Nachfassprozesses für Kundenfeedback',
                      company: 'Sparkassenverband LeerWittmund AdöR',
                    },
                    {
                      project: 'Aufbau Vertriebspartner-Stammdatenmanagement mit automatisiertem Prozess zur Datenvalidierung auf Open-Source-Basis',
                      company: 'EWETEL GmbH',
                    },
                  ];
                  const allItems = [...items, ...items];
                  return (
                    <div
                      className="flex gap-4 sm:gap-6 scroll-banner"
                      style={{ width: 'max-content' }}
                    >
                      {allItems.map((ref, idx) => (
                        <div key={idx} className="flex-shrink-0 w-[300px] sm:w-[380px] bg-gray-50 border border-gray-100 rounded-xl p-6 sm:p-8">
                          <p className="text-base font-semibold text-gray-900 mb-3 leading-snug">{ref.project}</p>
                          <p className="text-sm text-gray-400 font-mono">{ref.company}</p>
                          {ref.linkedIn && (
                            <a href="https://www.linkedin.com/in/stefan-wigbers-9b32b3119/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-4 text-xs text-gray-500 hover:text-gray-900 transition-colors">
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                              Empfehlung auf LinkedIn
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </section>


          {/* ===================== TECH STACK ===================== */}
          <section id="stack" className="py-24 md:py-32 bg-gray-900 text-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              {/* Header */}
              <div className="max-w-2xl mb-16">
                <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-500 mb-4">Tech Stack</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                  Open-Source AI-Infrastruktur, die skaliert
                </h2>
                <p className="text-gray-400 leading-relaxed">
                  Kein Vendor-Lock-in, keine versteckten Kosten. Jede Komponente ist austauschbar, jede Entscheidung nachvollziehbar — und alles wird per Infrastructure-as-Code provisioniert.
                </p>
              </div>

              {/* Architecture Diagram — Hub & Spoke with n8n center */}
              {/* Architecture Diagram — Desktop: Hub & Spoke, Mobile: Vertical Flow */}
              <div className="mb-16 bg-gray-800/50 border border-gray-700 rounded-xl p-5 sm:p-6 md:p-10">

                {/* === MOBILE: Vertical flow (visible < md) === */}
                <div className="md:hidden space-y-4">
                  {/* Data Sources */}
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-gray-600 mb-2">Datenquellen</p>
                    <div className="grid grid-cols-3 gap-2">
                      {['E-Mails & Tickets', 'Dokumente & PDFs', 'APIs & Webhooks'].map(s => (
                        <div key={s} className="bg-gray-900 border border-gray-700 rounded px-2 py-2 text-center">
                          <p className="text-[11px] text-gray-400">{s}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Arrow down */}
                  <div className="flex justify-center">
                    <div className="flex flex-col items-center">
                      <div className="w-px h-4 bg-gray-500" />
                      <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[8px] border-t-gray-500" />
                    </div>
                  </div>

                  {/* Center: n8n */}
                  <div className="bg-[#1E40AF] border-2 border-[#3B6FF5] rounded-xl px-5 py-4 text-center shadow-[0_0_30px_rgba(30,64,175,0.3)]">
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-blue-300 mb-1">Orchestration Layer</p>
                    <p className="text-lg font-bold text-white mb-1">n8n + Temporal</p>
                    <p className="text-xs text-blue-200">Workflows · Durable Execution · AI-Agents · 400+ Integrationen</p>
                    <div className="mt-3 flex gap-1.5 justify-center flex-wrap">
                      {['Trigger', 'Agent', 'Tool-Call', 'LangChain', 'Human-in-the-Loop'].map(t => (
                        <span key={t} className="text-[10px] font-mono bg-white/10 text-blue-200 px-2 py-0.5 rounded">{t}</span>
                      ))}
                    </div>
                  </div>

                  {/* Arrow down */}
                  <div className="flex justify-center">
                    <div className="flex flex-col items-center">
                      <div className="w-px h-4 bg-gray-500" />
                      <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[8px] border-t-gray-500" />
                    </div>
                  </div>

                  {/* Outputs */}
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-gray-600 mb-2">Aktionen</p>
                    <div className="grid grid-cols-3 gap-2">
                      {['CRM-Updates', 'Alerts', 'Reports'].map(s => (
                        <div key={s} className="bg-gray-900 border border-gray-700 rounded px-2 py-2 text-center">
                          <p className="text-[11px] text-gray-400">{s}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Arrow down */}
                  <div className="flex justify-center">
                    <div className="flex flex-col items-center">
                      <div className="w-px h-4 bg-gray-500" />
                      <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[8px] border-t-gray-500" />
                    </div>
                  </div>

                  {/* Supporting services */}
                  <div className="space-y-2">
                    <div className="bg-gray-900 border border-[#1E40AF]/30 rounded-lg p-4 text-center">
                      <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-gray-600 mb-1.5">LLM-Gateway (DSGVO)</p>
                      <p className="text-sm font-semibold text-[#6B9FFF] mb-1">vLLM + LiteLLM</p>
                      <p className="text-[11px] text-gray-500">Budget-Limits · Fallback-Routing · Rate-Limiting</p>
                    </div>
                    <div className="bg-gray-900 border border-[#1E40AF]/30 rounded-lg p-4 text-center">
                      <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-gray-600 mb-1.5">LLM-Observability</p>
                      <p className="text-sm font-semibold text-[#6B9FFF] mb-1">Langfuse</p>
                      <p className="text-[11px] text-gray-500">Traces · Kosten · Latenz · Eval-Scores</p>
                    </div>
                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 text-center">
                      <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-gray-600 mb-1.5">Infra-Monitoring</p>
                      <p className="text-sm font-semibold text-white mb-1">Grafana + Prometheus</p>
                      <p className="text-[11px] text-gray-500">Dashboards · Alerting · SLA-Tracking</p>
                    </div>
                  </div>

                  {/* Arrow down */}
                  <div className="flex justify-center">
                    <div className="flex flex-col items-center">
                      <div className="w-px h-4 bg-gray-500" />
                      <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[8px] border-t-gray-500" />
                    </div>
                  </div>

                  {/* Foundation */}
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
                    <div className="bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-center">
                      <p className="text-[10px] font-mono text-gray-600 mb-1">Infrastructure as Code</p>
                      <p className="text-sm font-semibold text-white">OpenTofu</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {['EU-Cloud / On-Prem', 'Netzwerk & Firewall', 'Container Runtime', 'Secrets & IAM'].map(s => (
                        <div key={s} className="bg-gray-900 border border-gray-700 rounded px-2 py-2 text-center">
                          <p className="text-[11px] text-gray-500">{s}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* === DESKTOP: Hub & Spoke (visible >= md) === */}
                <div className="hidden md:block">
                  {/* Row 1: Data Sources + Business Tools */}
                  <div className="grid grid-cols-[1fr_auto_2fr_auto_1fr] items-center gap-0 mb-1">
                    {/* Left: Data Sources */}
                    <div>
                      <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-gray-600 mb-2">Datenquellen</p>
                      <div className="space-y-2">
                        {['E-Mails & Tickets', 'Dokumente & PDFs', 'APIs & Webhooks'].map(s => (
                          <div key={s} className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-center">
                            <p className="text-xs text-gray-400">{s}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Arrow left → center */}
                    <div className="flex items-center px-2 self-center mt-6">
                      <div className="w-6 h-px bg-gray-500" />
                      <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[8px] border-l-gray-500" />
                    </div>

                    {/* Center: n8n */}
                    <div className="relative self-center mt-6">
                      <div className="bg-[#1E40AF] border-2 border-[#3B6FF5] rounded-xl px-6 py-5 text-center shadow-[0_0_30px_rgba(30,64,175,0.3)]">
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-blue-300 mb-1">Orchestration Layer</p>
                        <p className="text-xl font-bold text-white mb-1">n8n + Temporal</p>
                        <p className="text-xs text-blue-200">Workflows · Durable Execution · AI-Agents · 400+ Integrationen</p>
                        <div className="mt-3 flex gap-2 justify-center flex-wrap">
                          {['Trigger', 'Agent', 'Tool-Call', 'LangChain', 'Human-in-the-Loop'].map(t => (
                            <span key={t} className="text-[10px] font-mono bg-white/10 text-blue-200 px-2 py-0.5 rounded">{t}</span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Arrow center → right */}
                    <div className="flex items-center px-2 self-center mt-6">
                      <div className="w-6 h-px bg-gray-500" />
                      <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[8px] border-l-gray-500" />
                    </div>

                    {/* Right: Outputs */}
                    <div>
                      <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-gray-600 mb-2">Aktionen</p>
                      <div className="space-y-2">
                        {['CRM-Updates', 'Benachrichtigungen', 'Report-Generierung'].map(s => (
                          <div key={s} className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-center">
                            <p className="text-xs text-gray-400">{s}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Vertical connectors from n8n down */}
                  <div className="grid grid-cols-[1fr_auto_2fr_auto_1fr] items-start gap-0">
                    <div />
                    <div />
                    <div className="flex justify-around px-6 py-1">
                      {[0,1,2].map(i => (
                        <div key={i} className="flex flex-col items-center">
                          <div className="w-px h-6 bg-gray-500" />
                          <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[8px] border-t-gray-500" />
                        </div>
                      ))}
                    </div>
                    <div />
                    <div />
                  </div>

                  {/* Row 2: Supporting services */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-900 border border-[#1E40AF]/30 rounded-lg p-4 text-center">
                      <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-gray-600 mb-1.5">LLM-Gateway (DSGVO)</p>
                      <p className="text-sm font-semibold text-[#6B9FFF] mb-1">vLLM + LiteLLM</p>
                      <p className="text-[11px] text-gray-500">Budget-Limits · Fallback-Routing · Rate-Limiting</p>
                    </div>
                    <div className="bg-gray-900 border border-[#1E40AF]/30 rounded-lg p-4 text-center">
                      <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-gray-600 mb-1.5">LLM-Observability</p>
                      <p className="text-sm font-semibold text-[#6B9FFF] mb-1">Langfuse</p>
                      <p className="text-[11px] text-gray-500">Traces · Kosten · Latenz · Eval-Scores</p>
                    </div>
                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 text-center">
                      <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-gray-600 mb-1.5">Infra-Monitoring</p>
                      <p className="text-sm font-semibold text-white mb-1">Grafana + Prometheus</p>
                      <p className="text-[11px] text-gray-500">Dashboards · Alerting · SLA-Tracking</p>
                    </div>
                  </div>

                  {/* Vertical connector to foundation */}
                  <div className="flex justify-center py-1">
                    <div className="flex flex-col items-center">
                      <div className="w-px h-5 bg-gray-500" />
                      <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[8px] border-t-gray-500" />
                    </div>
                  </div>

                  {/* Row 3: Foundation */}
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 flex items-center gap-4">
                    <div className="bg-gray-900 border border-gray-600 rounded-lg px-5 py-3 text-center flex-shrink-0">
                      <p className="text-[10px] font-mono text-gray-600 mb-1">Infrastructure as Code</p>
                      <p className="text-sm font-semibold text-white">OpenTofu</p>
                    </div>
                    <div className="flex-1 flex items-center gap-3">
                      <div className="w-6 h-px bg-gray-600 flex-shrink-0" />
                      <div className="flex-1 grid grid-cols-4 gap-2">
                        {['EU-Cloud / On-Prem', 'Netzwerk & Firewall', 'Container Runtime', 'Secrets & IAM'].map(s => (
                          <div key={s} className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-center">
                            <p className="text-[11px] text-gray-500">{s}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* Detail Cards */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-800 rounded-xl overflow-hidden">
                {[
                  {
                    title: 'Infrastruktur provisionieren',
                    tool: 'OpenTofu',
                    desc: 'Infrastructure-as-Code: Gesamte Infrastruktur versioniert, reproduzierbar und in Minuten aufgebaut. Der offene Terraform-Fork ohne Vendor-Lock-in.',
                    detail: 'Open Source, Terraform-kompatibel'
                  },
                  {
                    title: 'Low-Code Workflows',
                    tool: 'n8n',
                    desc: 'Visuelle Workflow-Automatisierung mit nativer AI-Agent-Unterstützung. Verbindet Ihre bestehenden Systeme und macht KI-Prozesse nachvollziehbar.',
                    detail: 'Self-hosted, 400+ Integrationen'
                  },
                  {
                    title: 'Geschäftskritische Workflows',
                    tool: 'Temporal',
                    desc: 'Durable Execution für geschäftskritische Prozesse mit hohem Durchsatz. Garantierte Ausführung, automatische Retries und native Unterstützung für lang laufende Workflows.',
                    detail: 'Open Source, hochverfügbar & skalierbar'
                  },
                  {
                    title: 'LLMs DSGVO-konform betreiben',
                    tool: 'vLLM + LiteLLM',
                    desc: 'Open-Source-Modelle auf eigener Infrastruktur. LiteLLM als Gateway mit Budget-Limits, Rate-Limiting und Fallback-Routing.',
                    detail: 'On-Premise oder EU-Cloud'
                  },
                  {
                    title: 'LLM-Monitoring & Evaluation',
                    tool: 'Langfuse',
                    desc: 'Observability für jeden LLM-Call: Kosten, Latenz, Qualität. Traces zeigen, warum ein Agent eine Entscheidung getroffen hat.',
                    detail: 'Open Source, EU-hosted möglich'
                  },
                  {
                    title: 'Infrastruktur-Monitoring',
                    tool: 'Grafana + Prometheus',
                    desc: 'Echtzeit-Dashboards für alle Systeme. Alerting bei Anomalien.',
                    detail: 'Battle-tested, unbegrenzt skalierbar'
                  },
                ].map((item) => (
                  <div key={item.tool} className="bg-gray-900 p-7 md:p-8 flex flex-col">
                    <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-gray-600 mb-3">{item.title}</p>
                    <p className="text-lg font-semibold text-white mb-2">{item.tool}</p>
                    <p className="text-sm text-gray-400 leading-relaxed flex-1 mb-4">{item.desc}</p>
                    <p className="text-xs font-mono text-gray-600">{item.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>


          {/* ===================== PAKETE ===================== */}
          <section id="pakete" className="py-24 md:py-32 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-300 mb-4">Leistungen</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 max-w-xl">
                Zwei Pakete. Ein Ziel: Ihre IT modernisieren.
              </h2>
              <p className="text-gray-500 max-w-xl mb-16 leading-relaxed">
                Paket 1 ist BAFA-förderfähig und liefert den konkreten Plan inkl. Förderstrategie. Paket 2 setzt ihn um — ebenfalls förderfähig, im Parallelbetrieb, ohne Produktivitätsverlust.
              </p>

              <div className="grid lg:grid-cols-2 gap-8">
                {/* Paket 1 */}
                <div className="border-2 border-[#1E40AF] rounded-xl overflow-hidden">
                  <div className="bg-[#1E40AF] px-5 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-blue-200">Paket 1</p>
                      <p className="text-lg sm:text-xl font-bold text-white">Migrationsfahrplan</p>
                    </div>
                    <span className="text-xs font-mono bg-white/10 text-white px-3 py-1.5 rounded-full backdrop-blur-sm self-start sm:self-auto">BAFA-förderfähig</span>
                  </div>
                  <div className="p-5 sm:p-8">
                    <div className="space-y-5">
                      {[
                        { title: 'Systemlandschaft erfassen', desc: 'KI-Readiness-Check: Schnittstellen, Datenqualität, Integrationen' },
                        { title: 'Prozesse dokumentieren', desc: 'Ist-Aufnahme der aktuellen Abläufe und Identifikation von Automatisierungspotenzial' },
                        { title: 'Open-Source-Alternativen erarbeiten', desc: 'Konkrete Tool-Empfehlungen mit Migrationsaufwand und Risikobewertung' },
                        { title: 'Förderstrategie entwickeln', desc: 'Passende Programme identifizieren (BAFA, NBank, Digitalbonus, etc.)' },
                        { title: 'ROI-Kalkulation', desc: 'Gegenüberstellung: aktuelle Lizenzkosten vs. Open-Source-Betriebskosten' },
                      ].map((item, i) => (
                        <div key={i} className="flex gap-4">
                          <span className="text-[13px] font-mono text-gray-300 pt-0.5 flex-shrink-0 w-5">{String(i + 1).padStart(2, '0')}</span>
                          <div>
                            <p className="font-medium text-gray-900 text-[15px] mb-0.5">{item.title}</p>
                            <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-8 pt-6 border-t border-gray-100">
                      <button
                        onClick={() => window.open(BOOKING_URL, '_blank')}
                        className="w-full inline-flex items-center justify-center gap-2.5 bg-gray-900 text-white px-7 py-3.5 text-[15px] font-medium rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        Erstgespräch vereinbaren
                      </button>
                    </div>
                  </div>
                </div>

                {/* Paket 2 */}
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-5 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-400">Paket 2</p>
                      <p className="text-lg sm:text-xl font-bold text-gray-900">Implementierung</p>
                    </div>
                    <span className="text-xs font-mono bg-gray-200 text-gray-600 px-3 py-1.5 rounded-full self-start sm:self-auto">Förderfähig</span>
                  </div>
                  <div className="p-5 sm:p-8">
                    <div className="space-y-5">
                      {[
                        { title: 'Demo Day', desc: 'Testen Sie Ihre neue Open-Source-AI-Infrastruktur auf Herz und Nieren' },
                        { title: 'Migration im Parallelbetrieb', desc: 'Altes und neues System laufen parallel — kein Risiko, kein Ausfall' },
                        { title: 'User-Schulungen', desc: 'Praxisnahe Einführung für Ihr Team mit Dokumentation und Ansprechpartner' },
                        { title: 'Monitoring & Optimierung', desc: 'Dashboards, Alerting und kontinuierliche Verbesserung nach Go-Live' },
                        { title: 'Support in der Übergangsphase', desc: 'Persönlicher Ansprechpartner bis Ihr Team eigenständig arbeitet' },
                        { title: 'Förderung anwenden', desc: 'Die in Paket 1 erarbeitete Förderstrategie wird umgesetzt — bis zu 50 % der Implementierungskosten förderfähig' },
                      ].map((item, i) => (
                        <div key={i} className="flex gap-4">
                          <span className="text-[13px] font-mono text-gray-300 pt-0.5 flex-shrink-0 w-5">{String(i + 1).padStart(2, '0')}</span>
                          <div>
                            <p className="font-medium text-gray-900 text-[15px] mb-0.5">{item.title}</p>
                            <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-8 pt-6 border-t border-gray-100">
                      <button
                        onClick={() => window.open(BOOKING_URL, '_blank')}
                        className="w-full inline-flex items-center justify-center gap-2.5 bg-white text-gray-900 border border-gray-200 px-7 py-3.5 text-[15px] font-medium rounded-lg hover:border-gray-400 transition-colors"
                      >
                        Mehr erfahren
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>


          {/* ===================== BEISPIELKALKULATION ===================== */}
          <section id="kalkulation" className="py-24 md:py-32 bg-[#fafafa] border-t border-gray-100">
            <div className="max-w-5xl mx-auto px-6 lg:px-8">
              <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-300 mb-4">Beispielkalkulation</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 max-w-2xl">
                So rechnet sich die Transformation
              </h2>
              <p className="text-gray-500 mb-12 max-w-2xl leading-relaxed">
                Szenario: Mittelständisches Unternehmen (50 MA) ersetzt drei zentrale SaaS-Tools durch Open-Source-Alternativen und automatisiert manuelle Prozesse mit KI-Workflows.
              </p>

              {/* ── Tabelle 1: Laufende Kosten Vorher / Nachher ── */}
              <div className="mb-10">
                <p className="text-xs font-mono uppercase tracking-[0.15em] text-gray-400 mb-3">1 — Laufende Kosten pro Jahr</p>
                <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left font-semibold text-gray-900 px-3 sm:px-5 py-3 text-xs sm:text-sm">Position</th>
                        <th className="text-right font-semibold text-gray-900 px-3 sm:px-5 py-3 whitespace-nowrap text-xs sm:text-sm">Vorher</th>
                        <th className="text-right font-semibold text-[#1E40AF] px-3 sm:px-5 py-3 whitespace-nowrap text-xs sm:text-sm">Nachher</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono text-[12px] sm:text-[13px]">
                      {[
                        { pos: 'CRM-Lizenz (z. B. HubSpot Pro)', before: '10.800', after: '—' },
                        { pos: 'Projektmanagement (z. B. Monday.com)', before: '7.200', after: '—' },
                        { pos: 'Automatisierung (z. B. Zapier / Make)', before: '4.800', after: '—' },
                        { pos: 'Manueller Aufwand (Datenpflege, Reports)', before: '18.000', after: '5.400' },
                      ].map((r, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="px-3 sm:px-5 py-3 font-sans text-gray-600 text-xs sm:text-sm">{r.pos}</td>
                          <td className="px-3 sm:px-5 py-3 text-right text-gray-900 whitespace-nowrap">{r.before} €</td>
                          <td className="px-3 sm:px-5 py-3 text-right text-gray-900 whitespace-nowrap">{r.after === '—' ? '—' : `${r.after} €`}</td>
                        </tr>
                      ))}
                      <tr className="border-b border-gray-200 bg-gray-50/50">
                        <td className="px-3 sm:px-5 py-3 font-sans text-gray-400 italic text-xs sm:text-sm" colSpan={3}>TCO Open Source</td>
                      </tr>
                      {[
                        { pos: 'Server-Infrastruktur (EU-Cloud)', before: '—', after: '2.400' },
                        { pos: 'Wartung & Updates', before: '—', after: '1.800' },
                        { pos: 'Backup & Security-Monitoring', before: '—', after: '600' },
                      ].map((r, i) => (
                        <tr key={`tco-${i}`} className="border-b border-gray-100">
                          <td className="px-3 sm:px-5 py-3 font-sans text-gray-600 text-xs sm:text-sm">{r.pos}</td>
                          <td className="px-3 sm:px-5 py-3 text-right text-gray-300 whitespace-nowrap">{r.before}</td>
                          <td className="px-3 sm:px-5 py-3 text-right text-gray-900 whitespace-nowrap">{r.after} €</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 border-t-2 border-gray-200">
                        <td className="px-3 sm:px-5 py-4 font-sans font-bold text-gray-900 text-xs sm:text-sm">Gesamt pro Jahr</td>
                        <td className="px-3 sm:px-5 py-4 text-right font-bold text-red-600 text-sm sm:text-base whitespace-nowrap">40.800 €</td>
                        <td className="px-3 sm:px-5 py-4 text-right font-bold text-[#1E40AF] text-sm sm:text-base whitespace-nowrap">10.200 €</td>
                      </tr>
                      <tr className="bg-[#1E40AF]/5">
                        <td className="px-3 sm:px-5 py-3 font-sans font-semibold text-[#1E40AF] text-xs sm:text-sm">Jährl. Einsparung</td>
                        <td className="px-3 sm:px-5 py-3" colSpan={2}>
                          <p className="text-right font-bold text-[#1E40AF] text-base sm:text-lg">30.600 €</p>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* ── Tabelle 2: Einmalige Investition & Förderung ── */}
              <div className="mb-10">
                <p className="text-xs font-mono uppercase tracking-[0.15em] text-gray-400 mb-3">2 — Einmalige Projektkosten & Förderung</p>

                {/* Mobile: Card layout */}
                <div className="sm:hidden space-y-3">
                  {[
                    { name: 'Paket 1: Migrationsfahrplan', desc: 'Analyse, OS-Alternativen, Förderstrategie, ROI-Kalkulation', brutto: '3.500', foerderung: '1.750', eigen: '1.750', hint: 'BAFA — 50 %, max. 1.750 € netto' },
                    { name: 'Paket 2: Implementierung & Schulung', desc: '3 Tool-Migrationen, KI-Workflows, Parallelbetrieb, Demo Day, Training', brutto: '45.000', foerderung: '22.500', eigen: '22.500', hint: 'go-digital (BMWi) — 50 %, max. 16.500 €' },
                  ].map((p, i) => (
                    <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="font-semibold text-gray-900 text-sm mb-1">{p.name}</p>
                      <p className="text-xs text-gray-400 mb-3">{p.desc}</p>
                      <div className="grid grid-cols-3 gap-2 text-center font-mono text-xs">
                        <div>
                          <p className="text-gray-400 mb-1">Brutto</p>
                          <p className="text-gray-900 font-semibold">{p.brutto} €</p>
                        </div>
                        <div>
                          <p className="text-[#1E40AF] mb-1">Förderung</p>
                          <p className="text-[#1E40AF] font-semibold">– {p.foerderung} €</p>
                        </div>
                        <div>
                          <p className="text-gray-400 mb-1">Eigenanteil</p>
                          <p className="text-gray-900 font-bold">{p.eigen} €</p>
                        </div>
                      </div>
                      <p className="text-[11px] text-gray-400 mt-2">→ {p.hint}</p>
                    </div>
                  ))}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center font-mono text-sm">
                      <span className="font-bold text-gray-900">Gesamt einmalig</span>
                      <span className="font-bold text-gray-900">24.250 €</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">48.500 € brutto – 24.250 € Förderung</p>
                  </div>
                </div>

                {/* Desktop: Table layout */}
                <div className="hidden sm:block bg-white border border-gray-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left font-semibold text-gray-900 px-5 py-3">Position</th>
                        <th className="text-right font-semibold text-gray-900 px-5 py-3">Brutto</th>
                        <th className="text-right font-semibold text-[#1E40AF] px-5 py-3">Förderung</th>
                        <th className="text-right font-semibold text-gray-900 px-5 py-3">Eigenanteil</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono text-[13px]">
                      <tr className="border-b border-gray-100">
                        <td className="px-5 py-3 font-sans text-gray-600">
                          Paket 1: Migrationsfahrplan
                          <span className="block text-xs text-gray-400 mt-0.5">Analyse, OS-Alternativen, Förderstrategie, ROI-Kalkulation</span>
                        </td>
                        <td className="px-5 py-3 text-right text-gray-900">3.500 €</td>
                        <td className="px-5 py-3 text-right text-[#1E40AF]">– 1.750 €</td>
                        <td className="px-5 py-3 text-right text-gray-900 font-semibold">1.750 €</td>
                      </tr>
                      <tr className="border-b border-gray-100 bg-gray-50/30">
                        <td className="px-5 py-2 font-sans text-xs text-gray-400 pl-8" colSpan={4}>
                          → BAFA „Förderung unternehmerischen Know-hows" — 50 %, max. 1.750 € netto (2× pro Jahr möglich)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="px-5 py-3 font-sans text-gray-600">
                          Paket 2: Implementierung & Schulung
                          <span className="block text-xs text-gray-400 mt-0.5">3 Tool-Migrationen, KI-Workflows, Parallelbetrieb, Demo Day, Training</span>
                        </td>
                        <td className="px-5 py-3 text-right text-gray-900">45.000 €</td>
                        <td className="px-5 py-3 text-right text-[#1E40AF]">– 22.500 €</td>
                        <td className="px-5 py-3 text-right text-gray-900 font-semibold">22.500 €</td>
                      </tr>
                      <tr className="border-b border-gray-100 bg-gray-50/30">
                        <td className="px-5 py-2 font-sans text-xs text-gray-400 pl-8" colSpan={4}>
                          → go-digital (BMWi) — 50 % auf Umsetzungsleistungen, max. 16.500 € für Modul „Digitale Geschäftsprozesse"
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 border-t-2 border-gray-200">
                        <td className="px-5 py-4 font-sans font-bold text-gray-900">Gesamt einmalig</td>
                        <td className="px-5 py-4 text-right font-bold text-gray-900 font-mono">48.500 €</td>
                        <td className="px-5 py-4 text-right font-bold text-[#1E40AF] font-mono">– 24.250 €</td>
                        <td className="px-5 py-4 text-right font-bold text-gray-900 font-mono text-base">24.250 €</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* ── Ergebnis-Box ── */}
              <div className="bg-white border-2 border-[#1E40AF] rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1E40AF]">
                      <th className="text-left font-semibold text-white px-3 sm:px-5 py-3" colSpan={2}>Zusammenfassung</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-[12px] sm:text-[13px]">
                    <tr className="border-b border-gray-100">
                      <td className="px-3 sm:px-5 py-3 font-sans text-gray-600 text-xs sm:text-sm">Jährl. Einsparung (laufend)</td>
                      <td className="px-3 sm:px-5 py-3 text-right font-semibold text-[#1E40AF] whitespace-nowrap">30.600 €</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="px-3 sm:px-5 py-3 font-sans text-gray-600 text-xs sm:text-sm">Eigenanteil (einmalig)</td>
                      <td className="px-3 sm:px-5 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">24.250 €</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#1E40AF]/5 border-t-2 border-[#1E40AF]/20">
                      <td className="px-3 sm:px-5 py-4 font-sans font-bold text-[#1E40AF]">Amortisation</td>
                      <td className="px-3 sm:px-5 py-4 text-right font-bold text-[#1E40AF] text-base sm:text-lg">~10 Monate</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <p className="text-xs text-gray-400 mt-6">
                * Beispielrechnung auf Basis typischer Projektgrößen. Tatsächliche Kosten, TCO und Förderhöhen variieren je nach Unternehmensgröße, Standort und Projektumfang. Förderprogramme unterliegen Verfügbarkeit und individuellen Antragsvoraussetzungen.
              </p>
            </div>
          </section>


          {/* ===================== ÜBER MICH ===================== */}
          <section id="ueber-mich" className="py-24 md:py-32 bg-[#fafafa] border-t border-gray-100">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
                {/* Photo */}
                <div className="lg:col-span-4">
                  <div className="lg:sticky lg:top-24">
                    <div className="rounded-xl overflow-hidden bg-gray-200 aspect-[4/3] sm:aspect-[3/4] max-w-[280px] sm:max-w-none mx-auto lg:mx-0">
                      <Image
                        src="https://wigbers-automation.de/1677940065818.jpg"
                        alt="Stefan Wigbers"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="mt-6 space-y-1 text-center lg:text-left">
                      <p className="text-xl font-bold text-gray-900">Stefan Wigbers</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="lg:col-span-8 space-y-16">
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-300 mb-4">Über mich</p>
                    <p className="text-xl md:text-2xl text-gray-900 leading-relaxed font-medium max-w-2xl">
                      Ich helfe mittelständischen Unternehmen, ihre IT-Landschaft zu modernisieren — pragmatisch, förderfähig und mit messbarem ROI.
                    </p>
                    <p className="text-gray-500 leading-relaxed mt-6 max-w-2xl">
                      Mein Ansatz: Teure SaaS-Abhängigkeiten durch leistungsfähige Open-Source-Alternativen ersetzen und repetitive Prozesse mit KI-Workflows automatisieren. Keine Buzzwords, sondern Tools, die funktionieren — und ein Plan, der aufgeht.
                    </p>
                  </div>

                  {/* Werdegang */}
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-300 mb-8">Werdegang</p>
                    <div className="space-y-0 border-l border-gray-200 ml-1">
                      {[
                        { year: '2025', role: 'Freelance — Open Source AI Transformation', where: '', desc: 'Spezialisierung auf Open-Source-Migrationen und KI-Workflow-Automatisierung für den Mittelstand.' },
                        { year: '2024', role: 'Senior Developer Data & Process Automation', where: 'Muuuh! Consulting GmbH, Osnabrück', desc: 'Projektleitung für Automatisierungslösungen mit KI-Integration. Technische Architektur und Delivery.' },
                        { year: '2022', role: 'Advanced Consultant RPA & BI', where: 'Muuuh! Consulting GmbH, Osnabrück', desc: 'Komplexe Automatisierungsprojekte und technische Architekturentscheidungen.' },
                        { year: '2021', role: 'Consultant RPA & BI', where: 'Muuuh! Consulting GmbH, Osnabrück', desc: 'Spezialisierung auf RPA-Implementierungen und Business-Intelligence-Lösungen.' },
                        { year: '2019', role: 'Werkstudent BI & RPA', where: 'Muuuh! Consulting GmbH, Osnabrück', desc: 'Einstieg in Business Intelligence und RPA. Erste produktive Automatisierungen.' },
                        { year: '2015', role: 'Studium Information Systems', where: 'Universität Osnabrück', desc: 'Grundlage für Geschäftsprozesse, IT-Systeme und analytisches Denken.' },
                      ].map((m) => (
                        <div key={m.year} className="flex gap-6 pl-6 relative">
                          <div className="absolute left-[-5px] top-[10px] w-[9px] h-[9px] rounded-full bg-gray-300" />
                          <div className="pb-8 flex-1">
                            <span className="text-xs font-mono text-gray-400">{m.year}</span>
                            <p className="font-medium text-gray-900 mt-1">{m.role}</p>
                            {m.where && <p className="text-sm text-gray-500 mt-0.5">{m.where}</p>}
                            <p className="text-sm text-gray-400 mt-1.5 leading-relaxed">{m.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>


          {/* ===================== FAQ ===================== */}
          <section id="faq" className="py-24 md:py-32 bg-white border-t border-gray-100">
            <div className="max-w-3xl mx-auto px-6 lg:px-8">
              <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-300 mb-4">FAQ</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">Häufige Fragen</h2>
              <Accordion items={faqItems} />
            </div>
          </section>


          {/* ===================== KONTAKT ===================== */}
          <section id="kontakt" className="py-24 md:py-32 bg-[#fafafa] border-t border-gray-100">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="grid lg:grid-cols-12 gap-16">
                {/* Left */}
                <div className="lg:col-span-5">
                  <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-300 mb-4">Kontakt</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    Lassen Sie uns über Ihre IT sprechen.
                  </h2>
                  <p className="text-gray-500 leading-relaxed mb-10">
                    Ob Fördermittelcheck, Migrationsfahrplan oder konkrete Umsetzung — der erste Schritt ist ein Gespräch.
                  </p>

                  <div className="space-y-4 mb-10">
                    <a href="mailto:stefan@wigbers-automation.de" className="flex items-center gap-3 text-gray-500 hover:text-gray-900 transition-colors text-[15px]">
                      <span className="font-mono text-xs text-gray-300 w-12">Email</span>
                      stefan@wigbers-automation.de
                    </a>
                    <a href="tel:+491607729767" className="flex items-center gap-3 text-gray-500 hover:text-gray-900 transition-colors text-[15px]">
                      <span className="font-mono text-xs text-gray-300 w-12">Tel</span>
                      +49 160 7729767
                    </a>
                  </div>

                  <button
                    onClick={() => window.open(BOOKING_URL, '_blank')}
                    className="inline-flex items-center gap-2.5 bg-gray-900 text-white px-7 py-3.5 text-[15px] font-medium rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    <Icon name="Calendar" size={17} />
                    Termin buchen
                  </button>
                </div>

                {/* Right — Form */}
                <div className="lg:col-span-7">
                  <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-8 md:p-10">
                    {isSubmitted ? (
                      <div className="text-center py-12">
                        <p className="text-xl font-semibold text-gray-900 mb-2">Nachricht gesendet.</p>
                        <p className="text-gray-400">Ich melde mich innerhalb von 24 Stunden bei Ihnen.</p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid sm:grid-cols-2 gap-5">
                          <Input label="Name" type="text" placeholder="Ihr Name" value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} error={formErrors.name} required />
                          <Input label="Firma" type="text" placeholder="Ihre Firma" value={formData.company} onChange={(e) => handleInputChange('company', e.target.value)} error={formErrors.company} required />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-5">
                          <Input label="E-Mail" type="email" placeholder="ihre.email@firma.de" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} error={formErrors.email} required />
                          <Input label="Telefon" type="tel" placeholder="+49 160 1234567" value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} error={formErrors.phone} required />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-2 block">Nachricht</label>
                          <textarea
                            value={formData.message}
                            onChange={(e) => handleInputChange('message', e.target.value)}
                            placeholder="Womit kann ich Ihnen helfen?"
                            rows={4}
                            className="flex w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow"
                            required
                          />
                          {formErrors.message && <p className="text-sm text-red-500 mt-1">{formErrors.message}</p>}
                        </div>
                        {formErrors.submit && (
                          <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                            <p className="text-sm text-red-600">{formErrors.submit}</p>
                          </div>
                        )}
                        <div className="flex justify-center py-1">
                          <Turnstile
                            key={turnstileKey}
                            siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                            onVerify={(token) => { setTurnstileToken(token); setTurnstileError(null); if (formErrors.turnstile) setFormErrors(prev => { const n = { ...prev }; delete n.turnstile; return n; }); }}
                            onError={() => { setTurnstileToken(null); setTurnstileError('Verifizierung fehlgeschlagen.'); }}
                            onExpire={() => { setTurnstileToken(null); setTurnstileError('Verifizierung abgelaufen.'); }}
                            theme="light" size="normal" language="de"
                          />
                        </div>
                        {(formErrors.turnstile || turnstileError) && <p className="text-sm text-red-500 text-center">{formErrors.turnstile || turnstileError}</p>}
                        <button
                          type="submit"
                          disabled={!turnstileToken || isSubmitting}
                          className="w-full bg-gray-900 text-white px-7 py-3.5 text-[15px] font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Voice Agent Modal */}
        <AnimatePresence>
          {showVoiceAgent && (
            <ElevenLabsAgentWrapper onClose={() => setShowVoiceAgent(false)} onContactClick={() => { setShowVoiceAgent(false); scrollToSection('kontakt'); }} />
          )}
        </AnimatePresence>

        {/* ===================== FOOTER ===================== */}
        <footer className="bg-gray-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-12 gap-8 pb-10 border-b border-white/10">
              <div className="col-span-2 md:col-span-5">
                <img src="/assets/images/wigbers_logo.svg" alt="Wigbers" className="h-8 w-auto brightness-0 invert mb-3" />
                <p className="text-sm text-white/40">Open Source AI Transformation</p>
              </div>
              <div className="col-span-2 md:col-span-3">
                <p className="text-xs font-mono uppercase tracking-[0.15em] text-white/30 mb-3">Kontakt</p>
                <div className="space-y-1.5 text-sm">
                  <a href="mailto:stefan@wigbers-automation.de" className="text-white/60 hover:text-white transition-colors block break-all sm:break-normal">stefan@wigbers-automation.de</a>
                  <a href="tel:+491607729767" className="text-white/60 hover:text-white transition-colors block">+49 160 7729767</a>
                </div>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs font-mono uppercase tracking-[0.15em] text-white/30 mb-3">Seite</p>
                <div className="space-y-1.5 text-sm">
                  <button onClick={() => scrollToSection('pakete')} className="text-white/60 hover:text-white transition-colors block">Leistungen</button>
                  <button onClick={() => scrollToSection('ueber-mich')} className="text-white/60 hover:text-white transition-colors block">Über mich</button>
                  <button onClick={() => scrollToSection('kontakt')} className="text-white/60 hover:text-white transition-colors block">Kontakt</button>
                </div>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs font-mono uppercase tracking-[0.15em] text-white/30 mb-3">Rechtliches</p>
                <div className="space-y-1.5 text-sm">
                  <a href="/datenschutz" className="text-white/60 hover:text-white transition-colors block">Datenschutz</a>
                  <a href="/impressum" className="text-white/60 hover:text-white transition-colors block">Impressum</a>
                  <button onClick={() => window.dispatchEvent(new CustomEvent('openCookieSettings'))} className="text-white/60 hover:text-white transition-colors block">Cookies</button>
                </div>
              </div>
            </div>
            <div className="pt-8 text-xs text-white/30">
              © {new Date().getFullYear()} Stefan Wigbers
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AgentOpsPage;
